import { Box } from "@mui/material";
import React, {
	ChangeEvent,
	ClipboardEvent,
	Dispatch,
	FC,
	Fragment,
	KeyboardEvent,
	ReactNode,
	SetStateAction,
	useRef
} from "react";
import NumberInput from "../../components/NumberInput";

interface IOTPInputProps {
	length: number;
	value: string;
	separator?: ReactNode;
	onChange: Dispatch<SetStateAction<string>>;
}

const OTPInput: FC<IOTPInputProps> = (props) => {
	const { separator, length, value, onChange } = props;

	const inputRefs = useRef<HTMLInputElement[]>(new Array(length).fill(null));

	function focusInput(targetIndex: number) {
		const targetInput = inputRefs.current[targetIndex];
		if (targetInput) targetInput.focus();
	}

	function selectInput(targetIndex: number) {
		const targetInput = inputRefs.current[targetIndex];
		if (targetInput) targetInput.select();
	}

	function handleKeyDown(event: KeyboardEvent<HTMLInputElement>, currentIndex: number) {
		switch (event.key) {
			case "ArrowUp":
			case "ArrowDown":
			case " ":
				event.preventDefault();
				break;

			case "ArrowLeft":
				event.preventDefault();
				if (currentIndex > 0) {
					focusInput(currentIndex - 1);
					selectInput(currentIndex - 1);
				}
				break;

			case "ArrowRight":
				event.preventDefault();
				if (currentIndex < length - 1) {
					focusInput(currentIndex + 1);
					selectInput(currentIndex + 1);
				}
				break;

			case "Delete":
				event.preventDefault();
				onChange((prevOTP) => prevOTP.slice(0, currentIndex) + prevOTP.slice(currentIndex + 1));
				break;

			case "Backspace":
				event.preventDefault();
				if (currentIndex > 0) {
					focusInput(currentIndex - 1);
					selectInput(currentIndex - 1);
				}

				onChange((prevOTP) => prevOTP.slice(0, currentIndex) + prevOTP.slice(currentIndex + 1));
				break;

			default:
				break;
		}
	}

	function handleChange(event: ChangeEvent<HTMLInputElement>, currentIndex: number) {
		const currentValue = event.target.value;
		let indexToEnter = 0;

		while (indexToEnter <= currentIndex) {
			if (inputRefs.current[indexToEnter].value && indexToEnter < currentIndex) {
				indexToEnter += 1;
			} else {
				break;
			}
		}

		onChange((prevOTP) => {
			const otpArray = prevOTP.split("");
			const lastValue = currentValue[currentValue.length - 1];
			otpArray[indexToEnter] = lastValue;
			return otpArray.join("");
		});

		if (currentValue !== "" && currentIndex < length - 1) {
			focusInput(currentIndex + 1);
		}
	}

	function handleClick(currentIndex: number) {
		selectInput(currentIndex);
	}

	function handlePaste(event: ClipboardEvent<HTMLInputElement>, currentIndex: number) {
		event.preventDefault();
		const { clipboardData } = event;

		if (clipboardData.types.includes("text/plain")) {
			let pastedText = clipboardData.getData("text/plain");
			pastedText = pastedText.substring(0, length).trim();

			let indexToEnter = 0;

			while (indexToEnter <= currentIndex) {
				if (inputRefs.current[indexToEnter].value && indexToEnter < currentIndex) {
					indexToEnter += 1;
				} else {
					break;
				}
			}

			const otpArray = value.split("");

			for (let index = indexToEnter; index < length; index++) {
				const lastValue = pastedText[index - indexToEnter] ?? " ";
				otpArray[index] = lastValue;
			}

			onChange(otpArray.join(""));
		}
	}

	return (
		<Box sx={{ display: "flex", gap: "1rem", alignItems: "center", justifyContent: "center" }}>
			{new Array(length).fill(null).map((_, index) => (
				<Fragment key={index}>
					<NumberInput
						autoComplete="off"
						variant="filled"
						color="primary"
						sx={{ width: "56px", height: "56px" }}
						autoFocus={index === 0}
						slotProps={{
							input: {
								disableUnderline: true,
								hiddenLabel: true,
								classes: { root: "login-input-root" }
							},
							htmlInput: {
								ref: (element: HTMLInputElement) => (inputRefs.current[index] = element),
								onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => handleKeyDown(event, index),
								onChange: (event: ChangeEvent<HTMLInputElement>) => handleChange(event, index),
								onClick: () => handleClick(index),
								onPaste: (event: ClipboardEvent<HTMLInputElement>) => handlePaste(event, index),
								value: value[index] ?? "",
								sx: { textAlign: "center" }
							}
						}}
					/>

					{separator && index === length - 1 ? null : separator}
				</Fragment>
			))}
		</Box>
	);
};

export default OTPInput;
